import React from 'react';

import PropTypes, {InferProps} from 'prop-types';
import {Box, FormControlLabel, Grid, GridSize} from '@material-ui/core';
import {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';

import {
    EfeAutocomplete,
    EfeCheckbox,
    EfeErrorMsg,
    EfeFieldText,
    EfeRadioButton,
    EfeRadioGroup,
} from 'components';
import {FieldShape} from 'commons/shapes';
import {EfeCatalogValue} from 'commons/models';
import {notEmpty} from 'commons/utils/Functions';
import {CATALOGS_TO_ORDER, Constants, TypeElement} from 'commons/constants';
import EfeDatepicker from 'components/efe-datepicker/EfeDatepicker';

EfeCommonField.propTypes = {
    field: PropTypes.shape(FieldShape).isRequired,
    index: PropTypes.number.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    values: PropTypes.any.isRequired,
    errors: PropTypes.any.isRequired,
};

function EfeCommonField(props: InferProps<typeof EfeCommonField.propTypes>) {
    const {field, index, setFieldValue, errors, values} = props;
    const valuesSafe: Record<string, any> = Object.create(null);
    Object.keys(values).forEach((key) => {
        valuesSafe[key] = values[key];
    });

    function handleTextChange(event: React.ChangeEvent<{value: unknown}>) {
        setFieldValue(field.attrName, event.target.value);
    }

    function handleSelectValue(value: any) {
        setFieldValue(field.attrName, value);
    }

    function handleDateChange(event: MaterialUiPickersDate) {
        setFieldValue(field.attrName, event);
    }

    function handleCheckbox(event: React.ChangeEvent<HTMLInputElement>) {
        setFieldValue(field.attrName, event.target.checked);
    }

    function getAutocompleteValue(): EfeCatalogValue | null {
        let value: EfeCatalogValue | undefined | null = null;
        if (field && field.catalogValues) {
            value = field.catalogValues.find(
                (value: EfeCatalogValue) => value.id === valuesSafe[field.attrName]
            );
            if (!value) {
                value = null;
            }
        }
        return value;
    }

    function handleRadioChange(event: React.ChangeEvent<HTMLInputElement>) {
        setFieldValue(event.target.value);
    }

    function validateDate(error: boolean, value: string) {
        if (error) {
            return true;
        }

        if (value && !Date.parse(value)) {
            return true;
        }

        return false;
    }

    function componentSelector() {
        switch (field.typeElement) {
            case TypeElement.TEXT: {
                return (
                    <EfeFieldText
                        fullWidth
                        required={field.required}
                        key={`field-${field.attrName}`}
                        id={field.attrName}
                        name={field.attrName}
                        label={field.label}
                        value={values[field.attrName]}
                        onChange={handleTextChange}
                        disabled={field.disable}
                        error={!!errors[field.attrName]}
                    />
                );
            }
            case TypeElement.NUMBER: {
                return (
                    <EfeFieldText
                        fullWidth
                        required={field.required}
                        key={`field-${field.attrName}`}
                        id={field.attrName}
                        name={field.attrName}
                        label={field.label}
                        value={values[field.attrName]}
                        onChange={handleTextChange}
                        error={!!errors[field.attrName]}
                        type={'number'}
                    />
                );
            }
            case TypeElement.EMAIL: {
                return (
                    <EfeFieldText
                        fullWidth
                        required={field.required}
                        key={`field-${field.attrName}`}
                        id={field.attrName}
                        name={field.attrName}
                        label={field.label}
                        value={values[field.attrName]}
                        onChange={handleTextChange}
                        error={!!errors[field.attrName]}
                        type={'email'}
                    />
                );
            }
            case TypeElement.SELECT: {
                let temp: Array<any> = field.catalogValues ? [...field.catalogValues] : [];
                if (
                    field.catalog &&
                    CATALOGS_TO_ORDER.findIndex((item) => item === field.catalog) > -1
                ) {
                    temp = temp.sort((a, b) => a.order - b.order);
                }
                return (
                    <EfeAutocomplete
                        key={`field-${field.attrName}`}
                        required={field.required}
                        labelText={`${field.label} ${field.required ? '*' : ''}`}
                        name={field.attrName}
                        id={field.attrName}
                        selectedValue={getAutocompleteValue()}
                        options={temp}
                        onValueChange={(value: any) => handleSelectValue(value)}
                        disabled={!field.catalogValues || field.disable}
                    />
                );
            }
            case TypeElement.DATEPICKER: {
                return (
                    <EfeDatepicker
                        key={`field-${field.attrName}`}
                        id={field.attrName}
                        name={field.attrName}
                        error={validateDate(!!errors[field.attrName], values[field.attrName])}
                        value={
                            notEmpty(values[field.attrName]) && values[field.attrName] !== ''
                                ? values[field.attrName]
                                : null
                        }
                        onChange={handleDateChange}
                        required={field.required}
                        format={Constants.DATE_FORMAT}
                        placeholder={field.label}
                    />
                );
            }
            case TypeElement.CHECKBOX: {
                return (
                    <FormControlLabel
                        control={
                            <EfeCheckbox
                                key={`field-${field.attrName}`}
                                name={field.attrName}
                                id={field.attrName}
                                onChange={handleCheckbox}
                                checked={values[field.attrName]}
                            />
                        }
                        label={`${field.label} ${field.required ? '*' : ''}`}
                    />
                );
            }
            case TypeElement.RADIO_GROUP: {
                return (
                    <EfeRadioGroup
                        label={field.label}
                        fieldName={field.attrName}
                        options={field.catalogValues}
                        value={values[field.attrName]}
                        inlineRadio={true}
                        labelPlacement={'start'}
                        radioLabelPlacement={'top'}
                        change={handleRadioChange}
                    />
                );
            }
            case TypeElement.RADIO_BUTTON: {
                return (
                    <EfeRadioButton
                        onChange={handleRadioChange}
                        checked={field.attrName == values[field.attrName]}
                        value={field.attrName}
                        name={field.label}
                        selectedValue={values[field.attrName]}
                    />
                );
            }
            default: {
                return (
                    <EfeFieldText
                        fullWidth
                        required={field.required}
                        key={`field-${field.attrName}`}
                        id={field.attrName}
                        name={field.attrName}
                        label={field.label}
                        value={values[field.attrName]}
                        onChange={handleTextChange}
                        error={!!errors[field.attrName]}
                    />
                );
            }
        }
    }

    return (
        <Grid item xs={field.gridSize as GridSize} key={`field-${index}-${field.attrName}`}>
            <Box m={2}>
                {componentSelector()}
                {errors[field.attrName] && <EfeErrorMsg message={errors[field.attrName]} />}
            </Box>
        </Grid>
    );
}

export default EfeCommonField;
